/**
 * Device class and texture budget (ARCHITECTURE.md §8.5, §8.6).
 *
 * The budget is measured in **decoded GPU bytes** (`w × h × 4`), never in
 * file size, so the class decided here picks the ceiling `MemoryBudget`
 * enforces.
 *
 * Everything is a pure function of `DeviceInputs`, so a test can pin a
 * phone without touching `navigator`.
 */

/** §8.5 device classes. */
export type DeviceClass = 'phone' | 'tablet' | 'desktop'

/** What `detectDevice` reads. Injectable so vitest never needs a real `navigator`. */
export interface DeviceInputs {
  /** CSS pixels of the viewport's shorter and longer side are derived from these. */
  innerWidth: number
  innerHeight: number
  devicePixelRatio: number
  /** `navigator.deviceMemory` in GB — absent on iOS and in Firefox. */
  deviceMemory?: number | undefined
  /** `navigator.hardwareConcurrency`. */
  hardwareConcurrency?: number | undefined
  userAgent: string
  /** `navigator.maxTouchPoints`. */
  maxTouchPoints?: number | undefined
  /** `matchMedia('(prefers-reduced-motion: reduce)').matches`. */
  prefersReducedMotion: boolean
  /** `matchMedia('(pointer: coarse)').matches`. */
  coarsePointer: boolean
}

export interface DeviceProfile {
  class: DeviceClass
  /** Decoded-texture ceiling in MiB (§8.5: 128 / 192 / 256). */
  textureBudgetMib: number
  /** Same number in bytes — what `MemoryBudget` actually counts. */
  textureBudgetBytes: number
  /** Renderer resolution: `min(devicePixelRatio, 2)` (§8.1). */
  resolution: number
  deviceMemoryGb: number | null
  viewport: { w: number; h: number }
  /** True when the viewport is taller than it is wide — the stage top-letterboxes (§8.6). */
  portrait: boolean
  reducedMotion: boolean
  touch: boolean
}

const MIB = 1024 * 1024

/** `options.texture_budget_mib` of every bundle (§4.6.4) — the same numbers, keyed by class. */
export const DEFAULT_TEXTURE_BUDGET_MIB: Record<DeviceClass, number> = {
  phone: 128,
  tablet: 192,
  desktop: 256,
}

const PHONE_UA = /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|Opera Mini|Mobile Safari/i
const TABLET_UA = /iPad|Android(?!.*Mobile)|Tablet|Silk|PlayBook/i
/** iPadOS 13+ reports a desktop Safari UA; it is a tablet when it also reports touch. */
const MAC_UA = /Macintosh/i

/** The inputs a browser gives us. Safe to call in jsdom; falls back to desktop defaults. */
export function readDeviceInputs(win: Window & typeof globalThis = globalThis as never): DeviceInputs {
  const nav = (win as { navigator?: Navigator & { deviceMemory?: number } }).navigator
  const mm = typeof win.matchMedia === 'function' ? win.matchMedia.bind(win) : null
  const query = (q: string): boolean => {
    try {
      return mm ? mm(q).matches : false
    } catch {
      return false
    }
  }
  return {
    innerWidth: Number(win.innerWidth) || 1280,
    innerHeight: Number(win.innerHeight) || 720,
    devicePixelRatio: Number(win.devicePixelRatio) || 1,
    deviceMemory: nav?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency,
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints,
    prefersReducedMotion: query('(prefers-reduced-motion: reduce)'),
    coarsePointer: query('(pointer: coarse)'),
  }
}

/** §8.5 classification: viewport + `navigator.deviceMemory` + UA, in that order of trust. */
export function classifyDevice(i: DeviceInputs): DeviceClass {
  const short = Math.min(i.innerWidth, i.innerHeight)
  const long = Math.max(i.innerWidth, i.innerHeight)
  const touch = (i.maxTouchPoints ?? 0) > 0 || i.coarsePointer

  if (PHONE_UA.test(i.userAgent)) return 'phone'
  // iPadOS masquerades as a Mac; a Mac with touch points is an iPad.
  if (TABLET_UA.test(i.userAgent) || (MAC_UA.test(i.userAgent) && (i.maxTouchPoints ?? 0) > 1)) return 'tablet'

  // No UA hint (or a spoofed one): the viewport decides, tightened by memory.
  if (short <= 480 || long <= 820) return touch ? 'phone' : short <= 480 ? 'phone' : 'tablet'
  if (touch && long <= 1366) return 'tablet'
  if ((i.deviceMemory ?? 8) <= 2) return 'phone'
  if ((i.deviceMemory ?? 8) <= 4 && touch) return 'tablet'
  return 'desktop'
}

export interface DeviceOverrides {
  class?: DeviceClass
  textureBudgetMib?: number
  reducedMotion?: boolean
  /** Per-bundle `options.texture_budget_mib` (§4.6.4) — the bundle wins over the table above. */
  budgetTable?: Partial<Record<DeviceClass, number>>
}

/**
 * Device profile for this browser. §8.5:
 * "phones with `deviceMemory ≤ 2` or an unknown value start at 128".
 * Unknown memory on a *desktop* is not suspicious, so only phones and
 * tablets are demoted for it.
 */
export function detectDevice(inputs: DeviceInputs, over: DeviceOverrides = {}): DeviceProfile {
  const cls = over.class ?? classifyDevice(inputs)
  const mem = typeof inputs.deviceMemory === 'number' && inputs.deviceMemory > 0 ? inputs.deviceMemory : null
  const mib = over.textureBudgetMib ?? over.budgetTable?.[cls] ?? DEFAULT_TEXTURE_BUDGET_MIB[cls]

  return {
    class: cls,
    textureBudgetMib: mib,
    textureBudgetBytes: Math.round(mib * MIB),
    resolution: Math.min(Math.max(1, inputs.devicePixelRatio), 2),
    deviceMemoryGb: mem,
    viewport: { w: inputs.innerWidth, h: inputs.innerHeight },
    portrait: inputs.innerHeight > inputs.innerWidth,
    reducedMotion: over.reducedMotion ?? inputs.prefersReducedMotion,
    touch: (inputs.maxTouchPoints ?? 0) > 0 || inputs.coarsePointer,
  }
}

/** Convenience for the player: read the live browser and classify in one step. */
export function currentDevice(over: DeviceOverrides = {}, win?: Window & typeof globalThis): DeviceProfile {
  return detectDevice(readDeviceInputs(win), over)
}

/** A safe, explicit desktop profile — the headless default in vitest. */
export function headlessDevice(over: DeviceOverrides = {}): DeviceProfile {
  return detectDevice(
    {
      innerWidth: 1280,
      innerHeight: 720,
      devicePixelRatio: 1,
      userAgent: 'headless',
      prefersReducedMotion: false,
      coarsePointer: false,
    },
    over,
  )
}
